// src/components/username/UsernameModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography, CircularProgress } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { API_BASE } from '../../utils/api';

const USERNAME_RE = /^[a-zA-Z0-9_.-]{3,20}$/;

export default function UsernameModal() {
  const { user, needsUsername, setNeedsUsername, updateUsername } = useAuth();
  const [value, setValue] = useState(user?.username || '');
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setValue(user?.username || '');
  }, [user]);

  // pre-validate format
  const formatOk = useMemo(() => USERNAME_RE.test(value || ''), [value]);

  async function checkAvailability(name) {
    if (!formatOk) { setAvailable(false); return; }
    setChecking(true);
    setError('');
    try {
      const token =
        localStorage.getItem('rv_access_token') ||
        localStorage.getItem('access_token') || '';
      const resp = await fetch(
        `${API_BASE}/api/user/username/check/${encodeURIComponent(name)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await resp.json();
      setAvailable(!!data?.available);
      if (!resp.ok && data?.error) setError(data.error);
    } catch {
      setAvailable(false);
      setError('Network error');
    } finally {
      setChecking(false);
    }
  }
  useEffect(() => {
    if (!needsUsername) return;
    if (!value) return;
    const t = setTimeout(() => checkAvailability(value), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, needsUsername]);

  async function onSave() {
    setError('');
    if (!formatOk) {
      setError("3–20 chars: letters, numbers, '.', '-', '_' only");
      return;
    }
    if (!available) {
      setError('Username already taken');
      return;
    }
    const res = await updateUsername(value);
    if (!res.success) {
      setError(res.error || 'Failed to save username');
      return;
    }
    setNeedsUsername(false);
  }

  function onSkip() {
    // Keep the current (placeholder) username for now; user can change later.
    setNeedsUsername(false);
  }

  if (!needsUsername) return null;

  return (
    <Dialog open={needsUsername} onClose={() => null} maxWidth="xs" fullWidth>
      <DialogTitle>Choose your username</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          You can keep the suggested one or customize it. Usernames are unique and case-insensitive.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          label="Username"
          value={value}
          onChange={(e) => setValue(e.target.value.trim())}
          error={!!error || (!formatOk && value.length > 0)}
          helperText={
            error || (!formatOk && value
              ? "3–20 chars: a–z, 0–9, '.', '-', '_'"
              : ' ')
          }
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, minHeight: 24 }}>
          {checking ? <CircularProgress size={16} /> : null}
          {!checking && value && formatOk && (
            <Typography
              variant="caption"
              color={available ? 'success.main' : 'error'}
            >
              {available ? 'Available' : 'Already taken'}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onSkip}>Skip for now</Button>
        <Button onClick={onSave} variant="contained">
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}
